import { activityAnchor, type ActivityEntry } from '@molt/shared';
import { Link2 } from 'lucide-react';
import type { MouseEvent, ReactElement } from 'react';

import { Avatar, IconButton, ProfileLink, RelativeTime, Tooltip } from '@/components';
import type { BoardCardOpenIntent } from '@/lib/board-card-return';
import { activityActorName, activitySentence } from '@/lib/activity';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';

export interface ActivityEntryItemProps {
  readonly entry: ActivityEntry;
  readonly showCard?: boolean | undefined;
  readonly showBoard?: boolean | undefined;
  readonly highlighted?: boolean | undefined;
  readonly newArrival?: boolean | undefined;
  readonly onOpenCard?: ((intent: BoardCardOpenIntent) => void) | undefined;
  readonly onCopyLink?: ((activityId: string) => void) | undefined;
}

export function ActivityEntryItem({
  entry,
  showCard = false,
  showBoard = false,
  highlighted = false,
  newArrival = false,
  onOpenCard,
  onCopyLink,
}: ActivityEntryItemProps): ReactElement {
  const { catalogue, t } = useInterfaceLanguage();
  const actor = activityActorName(t, entry);
  const anchor = activityAnchor(entry.id);
  // The reference when this viewer may use one, else the short link — the
  // server scoped `ref` per viewer, and this file consumes it, never composes
  // one (docs/notes/card-keys.md).
  const cardPath =
    entry.card?.navigable === true
      ? `/c/${entry.card.ref ?? entry.card.shortLink}#${anchor}`
      : null;
  const board =
    showBoard && 'board' in entry
      ? (entry.board as
          | { id: string; title: string; shortLink: string; navigable: boolean }
          | undefined)
      : undefined;

  function handleCardClick(event: MouseEvent<HTMLAnchorElement>) {
    if (onOpenCard === undefined || entry.card === null || cardPath === null) return;
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    event.preventDefault();
    onOpenCard({
      cardId: entry.card.id,
      path: cardPath,
      stripScrollLeft: 0,
      listScrollTop: 0,
    });
  }

  return (
    <article
      id={anchor}
      data-conversation-target={anchor}
      data-highlighted={highlighted}
      data-new-arrival={newArrival}
      className={cn(
        'group scroll-mt-24 border-t border-border py-3',
        /*
          `FE-04.17`. The tint is `--color-accent-soft`, and the RING is not
          decoration — it is what carries the signal.

          Measured: in light theme the tint separates from `--color-surface` by
          1.15:1 (oklab dE 0.051), and the constrained maximum is 1.16:1, because
          `--color-ink-muted` — the timestamp and the hover icon buttons, which
          sit directly on this background — must keep 4.5:1. That is LESS than
          the 1.30:1 `border-border` hairline already drawn between every pair of
          rows, so the tint alone is a weaker signal than the rule it has to
          distinguish itself from.

          `styles.css` has met this shape once already and recorded the remedy:
          the dark modal scrim "separates nothing ... which is why FE-04.10's
          Dialog carries a mandatory 1px --color-border-strong edge". Same
          answer here. `--color-accent` measures 4.65:1 light / 4.69:1 dark on
          `--color-accent-soft`, well past 1.4.11's 3:1, and it matches the
          `border-accent` the reaction chip and template badge already wear over
          the same ground.

          Each utility carries its own variant prefix rather than sharing one:
          `tailwind-merge` keys on the full class, so two differently-keyed
          utilities both survive `cn` and neither arm evicts the other.
        */
        'data-[highlighted=true]:rounded-card data-[highlighted=true]:bg-accent-soft data-[highlighted=true]:my-2 data-[highlighted=true]:px-2 data-[highlighted=true]:ring-1 data-[highlighted=true]:ring-accent',
        'data-[new-arrival=true]:rounded-card data-[new-arrival=true]:bg-accent-soft data-[new-arrival=true]:my-2 data-[new-arrival=true]:px-2 data-[new-arrival=true]:ring-1 data-[new-arrival=true]:ring-accent',
      )}
      aria-label={t('cardBack.activityEntry.articleLabel', { actor })}
    >
      <div className="flex items-start gap-3">
        {entry.actor === null ? (
          <Avatar profile={null} state="unknown" size="sm" decorative />
        ) : (
          <Avatar profile={entry.actor} size="sm" decorative />
        )}
        <div className="min-w-0 flex-1 text-sm">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {entry.viaSuperadmin === true ? (
              /*
                SA-7 — "a small 'Platform admin' badge beside the actor". The
                edge case is a layout rule: "one token wide; it must not push
                the actor name onto a second line". So the name and the badge
                are one unbreakable run — the row's `flex-wrap` may move the
                sentence down but never part them — the badge neither wraps nor
                shrinks, and the name truncates rather than breaking.

                ⚠ Not the `Badge` primitive, which is count-only, and no new
                `--text-*` token: `text-xs` is Tailwind's own, which
                `tailwind-merge` already reads as a size (see `cn.ts`).
              */
              <span className="flex min-w-0 max-w-full flex-nowrap items-center gap-2">
                {entry.actor === null ? (
                  <span className="min-w-0 truncate font-medium text-ink">{actor}</span>
                ) : (
                  <ProfileLink profile={entry.actor} className="min-w-0 truncate" />
                )}
                <span className="shrink-0 whitespace-nowrap rounded-full border border-border bg-surface px-2 py-0.5 text-xs font-medium text-ink-muted">
                  {t('cardBack.activityEntry.platformAdminBadge')}
                </span>
              </span>
            ) : entry.actor === null ? (
              <span className="font-medium text-ink">{actor}</span>
            ) : (
              <ProfileLink profile={entry.actor} />
            )}
            <span className="text-ink">{activitySentence(entry, catalogue)}</span>
            {newArrival ? (
              <span className="rounded-full border border-accent/35 bg-surface px-2 py-0.5 text-xs font-semibold text-accent">
                {t('cardBack.activityEntry.newBadge')}
              </span>
            ) : null}
            <RelativeTime value={entry.createdAt} className="text-ink-muted" />
            {onCopyLink === undefined ? null : (
              <span className="ml-auto opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                <Tooltip content={t('cardBack.activityEntry.copyTooltip')}>
                  <IconButton
                    label={t('cardBack.activityEntry.copyLabel')}
                    size="sm"
                    onClick={() => onCopyLink(entry.id)}
                  >
                    <Link2 aria-hidden="true" className="size-4" />
                  </IconButton>
                </Tooltip>
              </span>
            )}
          </div>
          {showCard && entry.card !== null ? (
            <div className="mt-1 text-xs text-ink-muted">
              {cardPath === null ? (
                <span>{entry.card.title}</span>
              ) : (
                <a
                  href={cardPath}
                  onClick={handleCardClick}
                  className="font-medium text-ink underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
                >
                  {entry.card.title}
                </a>
              )}
            </div>
          ) : null}
          {board !== undefined ? (
            <div className="mt-1 text-xs text-ink-muted">
              <span>{t('cardBack.activityEntry.boardPrefix')} </span>
              {board.navigable ? (
                <a
                  href={`/b/${board.shortLink}`}
                  className="font-medium text-ink underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
                >
                  {board.title}
                </a>
              ) : (
                <span>{board.title}</span>
              )}
            </div>
          ) : null}
          {entry.collapsedCount > 1 ? (
            <p className="mt-1 text-xs text-ink-muted">
              {t(
                'cardBack.activityEntry.collapsedChanges',
                { count: entry.collapsedCount },
                entry.collapsedCount,
              )}
            </p>
          ) : null}
          {entry.fieldOverride !== null ? (
            <div className="mt-2 rounded-card border border-border bg-surface-raised p-2 text-xs text-ink">
              <p className="font-medium">
                {t(`cardBack.activityEntry.override.${entry.fieldOverride.field}`)}
              </p>
              <p className="mt-1 whitespace-pre-wrap text-ink-muted">
                {entry.fieldOverride.overriddenValue ??
                  t('cardBack.activityEntry.override.empty')}
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
}
